import { useState, useCallback, useRef } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ConfirmOptions {
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
}

interface ConfirmDialogProps {
  isOpen: boolean;
  options: ConfirmOptions;
  onConfirm: () => void;
  onCancel: () => void;
  onOpenChange: (open: boolean) => void;
}

export function ConfirmDialog({ isOpen, options, onConfirm, onCancel, onOpenChange }: ConfirmDialogProps) {
  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{options.title}</AlertDialogTitle>
          <AlertDialogDescription>
            {options.description || "Это действие нельзя будет отменить."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>
            {options.cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className={
              options.variant === "destructive"
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : ""
            }
          >
            {options.confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function useConfirm() {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({
    title: "Вы уверены?",
    confirmText: "Продолжить",
    cancelText: "Отмена",
    variant: "default",
  });

  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((optionsOrTitle: string | ConfirmOptions) => {
    const opts =
      typeof optionsOrTitle === "string"
        ? { title: optionsOrTitle }
        : optionsOrTitle;
    setOptions({
      title: opts.title,
      description: opts.description,
      confirmText: opts.confirmText || "Продолжить",
      cancelText: opts.cancelText || "Отмена",
      variant: opts.variant || "default",
    });
    setIsOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true);
    resolveRef.current = null;
    setIsOpen(false);
  }, []);

  const handleCancel = useCallback(() => {
    resolveRef.current?.(false);
    resolveRef.current = null;
    setIsOpen(false);
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      handleCancel();
    }
  }, [handleCancel]);

  // Готовый JSX-элемент с уже забинденными props — потребителю достаточно
  // просто вставить `{ConfirmationDialog}` в свой JSX, не передавая props.
  // Имя `ConfirmationDialog` сохранено для совместимости с существующими
  // вызовами (см. inline-editor.tsx).
  const ConfirmationDialog = (
    <ConfirmDialog
      isOpen={isOpen}
      options={options}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      onOpenChange={handleOpenChange}
    />
  );

  return {
    confirm,
    ConfirmationDialog,
    // Сохранена низкоуровневая раздача для редких случаев, когда потребителю
    // нужен полный контроль (передать кастомные обёртки, например).
    ConfirmDialog,
    isOpen,
    options,
    handleConfirm,
    handleCancel,
    handleOpenChange,
  };
}
